import { Box, Button, Typography } from "@mui/material";
import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";

import "./editBuildingConfig.css";
import { useGetSocietyConfigQuery } from "../../../../redux/reducers/settings.reducer";
import { BUILDING_TYPES_E, CUSTOM_ENTRY_BUILDING_NAME } from "../../../../utils/constants";
import { IBuildingDetails, IEditBuildingConfigurationDetails } from "../../../../interfaces";
import { useEditBuildingConfigurationMutation } from "../../../../redux/reducers/configuration.reducer";
import BuildingConfigComponent from "../../../../common/SocietyConfig/BuildingConfig";
import CustomEntriesComponent from "../../../../common/SocietyConfig/CustomEntries";
import { useAppDispatch } from "../../../../redux";
import { showFeedbackNotification } from "../../../../redux/reducers/notifications.reducer";

const EditBuildingConfig: FC = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const [selectedTab, setSelectedTab] = useState<"default" | "custom">("default");
	const [buildingsList, setBuildingsList] = useState<IBuildingDetails[]>([]);
	const [customEntriesList, setCustomEntriesList] = useState<string[]>([]);

	const { data: getSocietyConfigResponse, refetch: refetchSocietyConfigData } = useGetSocietyConfigQuery();
	const societyConfigData = useMemo(() => getSocietyConfigResponse?.results ?? [], [getSocietyConfigResponse?.results]);

	const initialBuildingDetails = useMemo<{
		buildingsList: IBuildingDetails[];
		customEntriesList: string[];
	}>(() => {
		const updatedInitialBuildingsList: IBuildingDetails[] = [];
		const updatedInitialCustomEntriesList: string[] = [];

		if (societyConfigData && societyConfigData.length > 0) {
			for (const buildingItem of societyConfigData[0].building_config) {
				if (buildingItem.building_type === BUILDING_TYPES_E.BUILDING) {
					updatedInitialBuildingsList.push({
						name: buildingItem.name,
						building_type: buildingItem.building_type,
						entries: buildingItem.entries.map(
							(entryItem) => `${buildingItem.name}${buildingItem.delimiter || "-"}${entryItem}`
						),
						number_of_floors: String(buildingItem.floors),
						flats_per_floor: String(buildingItem.flats),
						new_building: false,
						delimiter: buildingItem.delimiter || "-",
						show_floor_number: buildingItem.show_floor_number ?? false
					});
				} else if (buildingItem.building_type === BUILDING_TYPES_E.CUSTOM_ENTRIES) {
					updatedInitialCustomEntriesList.push(...buildingItem.entries);
				}
			}
		}

		return {
			buildingsList: updatedInitialBuildingsList,
			customEntriesList: updatedInitialCustomEntriesList
		};
	}, [societyConfigData]);

	const [
		editBuildingConfiguration,
		{
			isLoading: editBuildingConfigurationLoading,
			isSuccess: editBuildingConfigurationSuccess,
			error: editBuildingConfigurationError,
			reset: resetEditBuildingConfigurationState
		}
	] = useEditBuildingConfigurationMutation();

	function handleChangeSelectedTab(updatedValue: "default" | "custom") {
		setSelectedTab(updatedValue);
	}

	function handleDeleteBuilding(index: number) {
		if (index >= 0) {
			setBuildingsList((currentBuildingsList) => {
				const updatedBuildingsList = [...currentBuildingsList];
				updatedBuildingsList.splice(index, 1);
				return updatedBuildingsList;
			});
		}
	}

	function handleSaveBuildingDetails(index: number, updatedBuildingDetails: IBuildingDetails) {
		if (index >= 0) {
			const updatedBuildingsList = [...buildingsList];
			updatedBuildingsList[index] = updatedBuildingDetails;
			setBuildingsList(updatedBuildingsList);

			handleSubmitBuildingConfiguration(updatedBuildingsList);
		}
	}

	function handleAddNewBuilding() {
		const updatedBuildingsList = [...buildingsList];

		const updatedLength = updatedBuildingsList.push({
			name: "New Building",
			building_type: 0,
			entries: [],
			number_of_floors: "",
			flats_per_floor: "",
			new_building: true,
			delimiter: "-",
			show_floor_number: true
		});

		setBuildingsList(updatedBuildingsList);

		return updatedLength - 1;
	}

	function handleUpdateCustomEntries(updatedEntries: string[]) {
		setCustomEntriesList(updatedEntries);
	}

	function handleSubmitBuildingConfiguration(updatedBuildingsList: IBuildingDetails[]) {
		if (!societyConfigData || (societyConfigData && societyConfigData.length <= 0)) {
			return;
		}

		const finalBuildingConfig: IEditBuildingConfigurationDetails[] = [];

		for (const buildingItem of updatedBuildingsList) {
			finalBuildingConfig.push({
				name: buildingItem.name,
				building_type: buildingItem.building_type,
				entries: buildingItem.entries.map((entryItem) =>
					entryItem.replace(buildingItem.name, "").replace(buildingItem.delimiter, "")
				),
				floors: buildingItem.number_of_floors,
				flats: buildingItem.flats_per_floor,
				delimiter: buildingItem.delimiter,
				show_floor_number: buildingItem.show_floor_number
			});
		}

		if (customEntriesList.length > 0) {
			finalBuildingConfig.push({
				name: CUSTOM_ENTRY_BUILDING_NAME,
				building_type: BUILDING_TYPES_E.CUSTOM_ENTRIES,
				entries: customEntriesList
			});
		}

		if (finalBuildingConfig.length > 0) {
			editBuildingConfiguration({
				id: societyConfigData[0].id,
				data: {
					building_config: finalBuildingConfig
				}
			});
		}
	}

	function handleSubmitCustomEntries() {
		handleSubmitBuildingConfiguration(buildingsList);
	}

	const navigateToSystemScreen = useCallback(() => {
		navigate("/configuration/system");
	}, [navigate]);

	useEffect(() => {
		if (societyConfigData && societyConfigData.length > 0) {
			const updatedBuildingsList: IBuildingDetails[] = [];
			const updatedCustomEntriesList: string[] = [];

			for (const buildingItem of societyConfigData[0].building_config) {
				if (buildingItem.building_type === BUILDING_TYPES_E.BUILDING) {
					updatedBuildingsList.push({
						name: buildingItem.name,
						building_type: buildingItem.building_type,
						entries: buildingItem.entries.map(
							(entryItem) => `${buildingItem.name}${buildingItem.delimiter || "-"}${entryItem}`
						),
						number_of_floors: String(buildingItem.floors),
						flats_per_floor: String(buildingItem.flats),
						new_building: false,
						delimiter: buildingItem.delimiter || "-",
						show_floor_number: buildingItem.show_floor_number ?? false
					});
				} else if (buildingItem.building_type === BUILDING_TYPES_E.CUSTOM_ENTRIES) {
					updatedCustomEntriesList.push(...buildingItem.entries);
				}
			}

			setBuildingsList(updatedBuildingsList);
			setCustomEntriesList(updatedCustomEntriesList);
		}
	}, [societyConfigData]);

	useEffect(() => {
		if (editBuildingConfigurationSuccess) {
			refetchSocietyConfigData();
			resetEditBuildingConfigurationState();
			// navigateToSystemScreen();
		}
	}, [editBuildingConfigurationSuccess, refetchSocietyConfigData, resetEditBuildingConfigurationState]);

	useEffect(() => {
		if (editBuildingConfigurationError) {
			dispatch(
				showFeedbackNotification({
					message:
						"data" in editBuildingConfigurationError && editBuildingConfigurationError.data
							? String(editBuildingConfigurationError.data)
							: "Failed to update building configuration",
					severity: "error"
				})
			);

			resetEditBuildingConfigurationState();
		}
	}, [dispatch, editBuildingConfigurationError, resetEditBuildingConfigurationState]);

	const RenderTabContent: { [key in "default" | "custom"]: ReactNode } = {
		default: (
			<BuildingConfigComponent
				buildingsList={buildingsList}
				initialBuildingsList={initialBuildingDetails.buildingsList}
				saveButtonLoading={editBuildingConfigurationLoading}
				onDeleteBuilding={handleDeleteBuilding}
				onSaveBuilding={handleSaveBuildingDetails}
				onAddBuilding={handleAddNewBuilding}
				onCancelButtonClick={navigateToSystemScreen}
			/>
		),
		custom: (
			<CustomEntriesComponent
				customEntriesList={customEntriesList}
				initialCustomEntriesList={initialBuildingDetails.customEntriesList}
				saveButtonLoading={editBuildingConfigurationLoading}
				onUpdateCustomEntries={handleUpdateCustomEntries}
				onSave={handleSubmitCustomEntries}
				onCancelButtonClick={navigateToSystemScreen}
			/>
		)
	};

	return (
		<Box className="edit-building-config-screen-wrapper">
			<Typography variant="h6" color="var(--color-primary-main)">
				Property Configuration
			</Typography>

			<Box className="building-config-tabs-wrapper">
				<Button
					disableElevation
					variant={selectedTab === "default" ? "contained" : "outlined"}
					color="primary"
					startIcon={<DashboardCustomizeIcon />}
					onClick={() => handleChangeSelectedTab("default")}
				>
					Building Configuration
				</Button>

				<Button
					disableElevation
					variant={selectedTab === "custom" ? "contained" : "outlined"}
					color="primary"
					startIcon={<DashboardCustomizeIcon />}
					onClick={() => handleChangeSelectedTab("custom")}
				>
					Custom Entries
				</Button>
			</Box>

			{RenderTabContent[selectedTab]}
		</Box>
	);
};

export default EditBuildingConfig;
